import React from "react";
import { usePage } from "@inertiajs/react";
import SmartLink from "@/components/ui/SmartLink";
import { useSidebarCategories } from "@/hooks/useCategories";
import { Category } from "@/types";

interface CategorySidebarProps {
  fallbackCategories?: Category[];
  onItemClick?: () => void;
  className?: string;
  showHeader?: boolean;
  showLoadingState?: boolean;
  showErrorState?: boolean;
}

export const CategorySidebar: React.FC<CategorySidebarProps> = ({
  fallbackCategories = [],
  onItemClick,
  className = "",
  showHeader = true,
  showLoadingState = true,
  showErrorState = true,
}) => {
  // Get current page info for active state detection
  const page = usePage();
  const currentUrl = page.url;
  const currentRoute = page.component;

  // Use shared categories hook
  const {
    categoryTabs,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useSidebarCategories();

  // Fallback to prop categories if hook fails
  const displayCategories =
    categoryTabs.length > 0 ? categoryTabs : fallbackCategories;

  // Helper function to check if a category is active
  const isCategoryActive = (categorySlug?: string) => {
    if (!categorySlug) {
      // Home/Beranda is active when on root path
      return currentUrl === "/" || currentRoute === "Homepage";
    }

    // Check if current URL matches category slug
    return currentUrl.includes(`/category/${categorySlug}`);
  };

  // Handle item click
  const handleItemClick = (_categorySlug?: string) => {
    onItemClick?.();
  };

  return (
    <div className={`mb-6 ${className}`}>
      {/* Section Header */}
      {showHeader && (
        <h3 className="text-lg font-semibold text-white mb-4 px-2">Kategori</h3>
      )}

      {/* Loading State */}
      {categoriesLoading && showLoadingState && (
        <div className="flex flex-col gap-3">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-12 bg-white/10 rounded-lg animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Error State */}
      {categoriesError && showErrorState && (
        <div className="text-red-400 text-sm px-2 py-3 bg-red-500/10 rounded-lg mb-4">
          Error loading categories.{" "}
          {fallbackCategories.length > 0 && "Using fallback data."}
        </div>
      )}

      {/* Categories List */}
      {!categoriesLoading && (
        <div className="flex flex-col space-y-2">
          {displayCategories.map((cat: any) => {
            const href = cat.slug ? `/category/${cat.slug}` : "/";
            const isActive = isCategoryActive(cat.slug);
            const categoryName = cat.label || cat.name;

            return (
              <SmartLink
                key={cat.id || cat.slug || "home"}
                href={href}
                prefetchStrategy="hover"
                className={`
                  group relative flex items-center justify-between
                  px-4 py-3 rounded-xl transition-all duration-200
                  min-h-[48px] touch-manipulation
                  ${
                    isActive
                      ? "bg-white/20 text-white border border-white/30 shadow-lg"
                      : "bg-white/5 text-white/80 hover:bg-white/10 hover:text-white border border-transparent hover:border-white/20"
                  }
                `}
                onClick={() => handleItemClick(cat.slug)}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {/* Active indicator */}
                  {isActive && (
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3 flex-shrink-0" />
                  )}

                  {/* Category name */}
                  <span
                    className={`
                    font-medium text-sm truncate
                    ${isActive ? "font-semibold" : "font-normal"}
                  `}
                  >
                    {categoryName}
                  </span>
                </div>

                {/* News count badge (optional) */}
                {cat.newsCount && cat.newsCount > 0 && (
                  <span className="ml-2 px-2 py-1 bg-white/20 text-white text-xs rounded-full font-medium flex-shrink-0">
                    {cat.newsCount}
                  </span>
                )}

                {/* Arrow indicator */}
                <svg
                  className={`
                    w-4 h-4 ml-2 transition-transform duration-200 flex-shrink-0
                    ${isActive ? "text-white" : "text-white/60 group-hover:text-white"}
                    ${isActive ? "translate-x-1" : "group-hover:translate-x-0.5"}
                  `}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </SmartLink>
            );
          })}

          {/* Empty state */}
          {!categoriesLoading && displayCategories.length === 0 && (
            <div className="px-4 py-8 text-center">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <svg
                  className="w-8 h-8 mx-auto mb-3 text-white/40"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <div className="text-sm text-white/60">
                  Tidak ada kategori tersedia
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategorySidebar;
